/**
 * Interactive figures on Wave's landing pages and articles.
 *
 * scripts/build-pages.mjs renders each figure with its default state as plain
 * text, so it reads fine without JavaScript (and to search engines); this
 * module wires up the controls and draws the parts that need computing. The
 * maths comes from the engine itself - the size calculator calls the
 * planner's `targetBudget`, the HDR strip calls the tone mapper's CPU twin -
 * so a page can't promise something the converter won't do.
 *
 * Loaded by main.ts on landing pages that have a figure, and by its own
 * script tag on articles. Mounting is idempotent.
 */
import { targetBudget } from './engine/budget';
import { toneMapSignal } from './engine/tonemap-math';

const reduceMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SVG_NS = 'http://www.w3.org/2000/svg';

const svgEl = <K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
  text?: string,
): SVGElementTagNameMap[K] => {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  if (text != null) node.textContent = text;
  return node;
};

/** 65.5 → "1:05.5", 60 → "1:00". */
const clock = (seconds: number): string => {
  const tenths = Math.round(seconds * 10);
  const m = Math.floor(tenths / 600);
  const s = (tenths % 600) / 10;
  const whole = Math.floor(s);
  const frac = Math.round((s - whole) * 10);
  return `${m}:${String(whole).padStart(2, '0')}${frac ? `.${frac}` : ''}`;
};

/** Wires a row of `.opt-pill` radio buttons to `onPick`. */
const radioPills = (
  group: Element | null,
  onPick: (value: string) => void,
): void => {
  if (!group) return;
  const pills = Array.from(group.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
  for (const pill of pills) {
    pill.addEventListener('click', () => {
      for (const p of pills) p.setAttribute('aria-checked', p === pill ? 'true' : 'false');
      onPick(pill.dataset.value ?? '');
    });
  }
};

/** Runs `fn` once, the first time `el` scrolls into view. */
const onFirstView = (el: Element, fn: () => void): void => {
  let done = false;
  const run = (): void => {
    if (done) return;
    done = true;
    io?.disconnect();
    document.removeEventListener('scroll', check);
    fn();
  };
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) run();
      }, { rootMargin: '0px 0px -15% 0px' })
    : null;
  // Backstop, as in the article reveal script: an observer that never
  // delivers (a tab that isn't composited) must not leave the figure
  // half-finished, so scrolling also checks the position directly.
  const check = (): void => {
    if (!window.innerHeight) { run(); return; }
    const box = el.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) run();
  };
  if (!io) { run(); return; }
  io.observe(el);
  document.addEventListener('scroll', check, { passive: true });
  window.setTimeout(check, 1200);
};

// ─── Keyframes: where a fast cut really starts ────────────────────────

const TIMELINE_SECONDS = 10;

function mountKeyframes(fig: HTMLElement): void {
  const track = fig.querySelector<HTMLElement>('.xp-track');
  const range = fig.querySelector<HTMLInputElement>('input[type="range"]');
  const output = fig.querySelector<HTMLOutputElement>('output');
  const fastOut = fig.querySelector<HTMLElement>('[data-out="fast"]');
  const fastNote = fig.querySelector<HTMLElement>('[data-out="fast-note"]');
  const exactOut = fig.querySelector<HTMLElement>('[data-out="exact"]');
  if (!track || !range) return;

  let gop = Number(fig.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')?.dataset.value) || 2;

  // Geometry: 0-10 s across the box with a 24-unit margin each side. The box
  // is as wide as the figure in CSS pixels (up to 700), so the labels stay at
  // their real size on a phone instead of shrinking with the drawing.
  let width = 700;
  const x0 = 24;
  const x1 = (): number => width - 24;
  const xAt = (t: number): number => x0 + ((x1() - x0) * t) / TIMELINE_SECONDS;

  const svg = svgEl('svg', { viewBox: '0 0 700 132', class: 'xp-svg', role: 'presentation' });
  const frames = svgEl('g', { class: 'xp-frames' });
  const keyframes = svgEl('g', { class: 'xp-keys' });
  const extra = svgEl('rect', { class: 'xp-extra', y: 44, height: 40, rx: 4 });
  const kept = svgEl('rect', { class: 'xp-kept', y: 44, height: 40, rx: 4 });
  const fastMark = svgEl('g', { class: 'xp-mark xp-mark-fast' });
  fastMark.append(
    svgEl('line', { x1: 0, x2: 0, y1: 40, y2: 96 }),
    svgEl('text', { x: 0, y: 114, 'text-anchor': 'middle' }, 'fast cut'),
  );
  const exactMark = svgEl('g', { class: 'xp-mark xp-mark-exact' });
  exactMark.append(
    svgEl('line', { x1: 0, x2: 0, y1: 28, y2: 88 }),
    svgEl('text', { x: 0, y: 20, 'text-anchor': 'middle' }, 'you typed'),
  );
  const axis = svgEl('g', { class: 'xp-axis' });
  svg.append(kept, extra, frames, keyframes, fastMark, exactMark, axis);
  track.replaceChildren(svg);

  const drawFrames = (): void => {
    svg.setAttribute('viewBox', `0 0 ${width} 132`);
    frames.replaceChildren();
    keyframes.replaceChildren();
    axis.replaceChildren();
    for (let s = 0; s <= TIMELINE_SECONDS; s += 2) {
      axis.append(svgEl('text', { x: xAt(s), y: 130, 'text-anchor': 'middle' }, `0:${String(s).padStart(2, '0')}`));
    }
    // Ticks stand in for the frames between keyframes: one per tenth of a
    // second, or per fifth on a narrow screen so they don't merge.
    const step = width < 480 ? 2 : 1;
    for (let i = 0; i <= TIMELINE_SECONDS * 10; i += step) {
      const t = i / 10;
      const isKey = Math.abs(t / gop - Math.round(t / gop)) < 1e-6;
      if (isKey) {
        keyframes.append(svgEl('rect', { x: xAt(t) - 2.5, y: 48, width: 5, height: 32, rx: 2 }));
      } else {
        frames.append(svgEl('line', { x1: xAt(t), x2: xAt(t), y1: 56, y2: 72 }));
      }
    }
  };

  const update = (): void => {
    const start = Number(range.value);
    const fast = Math.floor(start / gop + 1e-9) * gop;
    const early = start - fast;
    kept.setAttribute('x', String(xAt(start)));
    kept.setAttribute('width', String(Math.max(0, x1() - xAt(start))));
    extra.setAttribute('x', String(xAt(fast)));
    extra.setAttribute('width', String(Math.max(0, xAt(start) - xAt(fast))));
    fastMark.setAttribute('transform', `translate(${xAt(fast)} 0)`);
    exactMark.setAttribute('transform', `translate(${xAt(start)} 0)`);
    // Keep the two labels from sitting on top of each other.
    fastMark.classList.toggle('is-same', early < 0.05);
    if (output) output.textContent = clock(start);
    range.setAttribute('aria-valuetext', clock(start));
    if (fastOut) fastOut.textContent = clock(fast);
    if (exactOut) exactOut.textContent = clock(start);
    if (fastNote) {
      fastNote.textContent = early < 0.05
        ? 'right on a keyframe · copied, takes seconds'
        : `${early.toFixed(1)} s early · copied, takes seconds`;
    }
  };

  radioPills(fig.querySelector('.opt-pills'), (value) => {
    gop = Number(value) || 2;
    drawFrames();
    update();
  });
  range.addEventListener('input', update);
  const fit = (): void => {
    const next = Math.round(Math.max(300, Math.min(700, track.clientWidth || 700)));
    if (next === width && svg.childElementCount && frames.childElementCount) return;
    width = next;
    drawFrames();
    update();
  };
  fit();
  if (typeof ResizeObserver === 'function') new ResizeObserver(fit).observe(track);
}

// ─── Budget: what a file size buys you ────────────────────────────────

/** Clip lengths the slider steps through, in seconds. */
const LENGTHS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 300, 420, 600];

// Bitrate bands from the planner's resolution step-down (shortEdgeForBitrate
// in src/engine/plan.ts), at 30 fps: the low edge of each band is where Wave
// drops to the next size.
const BANDS: Record<number, [number, number]> = {
  1080: [1_600_000, 3_500_000],
  720: [800_000, 1_600_000],
  480: [350_000, 800_000],
  360: [120_000, 350_000],
};

const fmtKbps = (bps: number): string =>
  bps >= 10_000_000 ? `${(bps / 1_000_000).toFixed(0)} Mbps` :
  bps >= 1_000_000 ? `${(bps / 1_000_000).toFixed(1)} Mbps` :
  `${Math.round(bps / 1000).toLocaleString('en-US')} kbps`;

function mountBudget(fig: HTMLElement): void {
  const range = fig.querySelector<HTMLInputElement>('input[type="range"]');
  const output = fig.querySelector<HTMLOutputElement>('output');
  const custom = fig.querySelector<HTMLInputElement>('input[type="number"]');
  const bigOut = fig.querySelector<HTMLElement>('[data-out="video"]');
  const verdictOut = fig.querySelector<HTMLElement>('[data-out="verdict"]');
  const audioOut = fig.querySelector<HTMLElement>('[data-out="audio"]');
  const barVideo = fig.querySelector<HTMLElement>('.xp-bar-video');
  const barAudio = fig.querySelector<HTMLElement>('.xp-bar-audio');
  if (!range) return;

  let sizeMb = Number(fig.dataset.size) || 20;

  const update = (): void => {
    const seconds = LENGTHS[Number(range.value)] ?? 60;
    const b = targetBudget(sizeMb * 1_000_000, seconds, 30, true);
    const full = b.totalBps / 0.95;
    const rawVideo = Math.max(0, b.totalBps - b.audioKbps * 1000);
    if (output) output.textContent = clock(seconds);
    range.setAttribute('aria-valuetext', clock(seconds));
    if (bigOut) bigOut.textContent = b.blocked ? 'Too small' : fmtKbps(b.videoBps);
    if (audioOut) audioOut.textContent = `${b.audioKbps} kbps`;
    if (barVideo) barVideo.style.width = `${(rawVideo / full) * 100}%`;
    if (barAudio) barAudio.style.width = `${((b.audioKbps * 1000) / full) * 100}%`;
    if (!verdictOut) return;
    if (b.blocked) {
      verdictOut.textContent = `${sizeMb} MB is too little for ${clock(seconds)} of video. Wave would ask for at least ${(b.minBytes / 1_000_000).toFixed(1)} MB, or a shorter clip.`;
      return;
    }
    // A 1080p source: the planner only ever lowers the size from there.
    const edge = Math.min(1080, b.shortEdge ?? 1080);
    if (edge === 1080 && b.videoBps >= BANDS[1080][1]) {
      verdictOut.textContent = 'Wave keeps 1080p, with bits to spare.';
      return;
    }
    const [low, high] = BANDS[edge];
    const position = (b.videoBps - low) / (high - low);
    const label = edge === 1080 ? 'keeps 1080p' : `encodes it at ${edge}p`;
    verdictOut.textContent = position < 0.4
      ? `Wave ${label}. Tight: talking heads and screen recordings stay clean, fast motion will show blocks.`
      : `Wave ${label}, which looks good for most footage at this rate.`;
  };

  radioPills(fig.querySelector('.opt-pills'), (value) => {
    sizeMb = Number(value) || sizeMb;
    if (custom) custom.value = '';
    update();
  });
  custom?.addEventListener('input', () => {
    const n = Number(custom.value);
    if (!Number.isFinite(n) || n <= 0) return;
    sizeMb = n;
    fig.querySelectorAll('.opt-pills [role="radio"]').forEach((p) => p.setAttribute('aria-checked', 'false'));
    update();
  });
  range.addEventListener('input', update);
  update();
}

// ─── HDR: the same clip, two ways ─────────────────────────────────────

// Colours from a typical HDR scene, as display light relative to HDR
// reference white (1.0 = 203 nits), in BT.709 primaries.
const HDR_PATCHES: { name: string; rgb: [number, number, number] }[] = [
  { name: 'Shadow', rgb: [0.03, 0.035, 0.05] },
  { name: 'Skin', rgb: [0.55, 0.36, 0.27] },
  { name: 'Grass', rgb: [0.09, 0.2, 0.04] },
  { name: 'Sky', rgb: [0.25, 0.45, 0.85] },
  { name: 'White shirt', rgb: [0.9, 0.9, 0.88] },
  { name: 'Sunlit cloud', rgb: [2.6, 2.5, 2.35] },
];

/** BT.709 display light → the HLG signal an iPhone would store for it:
 *  convert to BT.2020 primaries, undo the 1000-nit OOTF, apply the OETF. */
function hlgSignalFor(relative709: readonly number[]): [number, number, number] {
  const n = relative709.map((v) => v * 203);
  const r = 0.6274 * n[0] + 0.3293 * n[1] + 0.0433 * n[2];
  const g = 0.0691 * n[0] + 0.9195 * n[1] + 0.0114 * n[2];
  const b = 0.0164 * n[0] + 0.088 * n[1] + 0.8956 * n[2];
  const yd = Math.max(1e-6, 0.2627 * r + 0.678 * g + 0.0593 * b);
  const ys = Math.pow(yd / 1000, 1 / 1.2);
  const gain = 1000 * Math.pow(ys, 0.2);
  const oetf = (e: number): number => {
    const v = Math.min(1, Math.max(0, e));
    return v <= 1 / 12 ? Math.sqrt(3 * v) : 0.17883277 * Math.log(12 * v - 0.28466892) + 0.55991073;
  };
  return [oetf(r / gain), oetf(g / gain), oetf(b / gain)];
}

const css = (rgb: readonly number[]): string =>
  `rgb(${rgb.map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255)).join(' ')})`;

function mountHdr(fig: HTMLElement): void {
  const raw = fig.querySelector<HTMLElement>('.xp-hdr-raw');
  const mapped = fig.querySelector<HTMLElement>('.xp-hdr-mapped');
  if (!raw || !mapped) return;

  // The top row starts out showing the unconverted colours and, the first
  // time the figure scrolls into view, shifts to the tone-mapped ones - the
  // change the conversion makes, played once.
  const animate = !reduceMotion() && 'IntersectionObserver' in window;
  const targets: { el: HTMLElement; colour: string }[] = [];
  raw.replaceChildren();
  mapped.replaceChildren();
  HDR_PATCHES.forEach((patch, i) => {
    const signal = hlgSignalFor(patch.rgb);
    // An app that ignores HDR shows the stored signal as if it were ordinary
    // video: the brightness curve and the wider BT.2020 colours both land in
    // the wrong place.
    const before = document.createElement('span');
    before.style.backgroundColor = css(signal);
    const after = document.createElement('span');
    const colour = css(toneMapSignal(signal, 'hlg'));
    after.style.backgroundColor = animate ? css(signal) : colour;
    after.style.transitionDelay = `${i * 90}ms`;
    raw.append(before);
    mapped.append(after);
    targets.push({ el: after, colour });
  });

  if (animate) {
    onFirstView(fig, () => {
      // Let the unconverted colours paint first so the change is visible.
      window.setTimeout(() => {
        for (const t of targets) t.el.style.backgroundColor = t.colour;
      }, 350);
    });
  }
}

// ─── Mount ────────────────────────────────────────────────────────────

export function mountExplainers(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-explainer]').forEach((fig) => {
    if (fig.dataset.mounted) return;
    fig.dataset.mounted = 'true';
    fig.classList.add('is-live');
    const kind = fig.dataset.explainer;
    if (kind === 'keyframes') mountKeyframes(fig);
    else if (kind === 'budget') mountBudget(fig);
    else if (kind === 'hdr') mountHdr(fig);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountExplainers(), { once: true });
} else {
  mountExplainers();
}
